import { msg } from "@lingui/core/macro";
import { createRootRoute, Outlet, useLocation } from "@tanstack/solid-router";
import { createEffect, Suspense } from "solid-js";
import { Toaster } from "@/components/ui/toast";
import { InitialSyncOverlay } from "@/components/initial-sync-overlay";
import { GlassSurface } from "@/components/ui/glass-surface";
import { AlertCircle, RefreshCw } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { BootManager } from "@/providers/boot-manager";
import { PageTransition } from "@/components/ui/page-transition";
import { AppBackground } from "@/components/app-background";

function RootErrorComponent(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  createEffect(() => {
    console.error("[RootErrorComponent] Caught route error:", props.error);
  });
  return (
    <div class="min-h-dvh flex items-center justify-center p-4 bg-background">
      <GlassSurface class="max-w-md w-full p-8 flex flex-col items-center text-center space-y-6">
        <div class="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle class="h-8 w-8 text-destructive" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-muted-foreground text-sm">
            {props.error instanceof Error
              ? props.error.message
              : t(msg`An unexpected error has occurred.`)}
          </p>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            {t(msg`Reload page`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

function RootComponent() {
  const location = useLocation();

  return (
    <div class="relative min-h-dvh w-full">
      {/*
        Skeleton-first SIN overlay fullscreen: el fondo (AppBackground) se pinta de
        inmediato y el contenido carga directo. NO hay skeleton a pantalla completa
        (fallback={null}); cada sección/ruta muestra su propio skeleton GRANULAR
        mientras su query local resuelve (local-first: datos al instante). La sync
        inicial vive en `InitialSyncOverlay` como tarjeta flotante NO bloqueante.
      */}
      <AppBackground class="flex flex-col min-h-dvh">
        <Suspense fallback={null}>
          <BootManager>
            <InitialSyncOverlay>
              <PageTransition
                routeKey={(() => {
                  const path = location().pathname;
                  // Only trigger a root-level transition when switching between major shells
                  if (path.startsWith("/login") || path.startsWith("/register")) {
                    return "auth";
                  }
                  return "app";
                })()}
                class="flex flex-1 flex-col min-h-0 h-full"
              >
                <Outlet />
              </PageTransition>
            </InitialSyncOverlay>
          </BootManager>
        </Suspense>
        <Toaster />
      </AppBackground>
    </div>
  );
}

export const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
